import type { StateCreator } from 'zustand';
import type { StoreState } from './index';
import { brandingApi, fetchPublicBranding } from '../services/brandingApi';
import type { BrandingSettings, PublicBranding } from '../services/brandingApi';

export interface BrandingSlice {
  branding: BrandingSettings | null;
  brandingLoading: boolean;
  loadPublicBranding: () => Promise<void>;
  loadBranding: () => Promise<void>;
  updateBranding: (data: Partial<Pick<BrandingSettings, 'app_name' | 'primary_color' | 'accent_color'>>) => Promise<void>;
  uploadLogo: (file: File) => Promise<void>;
  applyTheme: () => void;
}

function lighten(hex: string, amount: number): string {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, ((n >> 16) & 0xff) + amount);
  const g = Math.min(255, ((n >> 8) & 0xff) + amount);
  const b = Math.min(255, (n & 0xff) + amount);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

export const createBrandingSlice: StateCreator<StoreState, [], [], BrandingSlice> = (set, get) => ({
  branding: null,
  brandingLoading: false,

  loadPublicBranding: async () => {
    try {
      const pub: PublicBranding = await fetchPublicBranding();
      set((s) => ({
        branding: {
          ...s.branding,
          app_name: pub.app_name,
          logo_url: pub.logo_url,
          favicon_url: s.branding?.favicon_url ?? null,
          primary_color: s.branding?.primary_color ?? '#646cff',
          accent_color: s.branding?.accent_color ?? '#646cff',
        },
      }));
      get().applyTheme();
    } catch {
      // fallback: keep default
    }
  },

  loadBranding: async () => {
    set({ brandingLoading: true });
    try {
      const b = await brandingApi.fetchBranding();
      set({ branding: b });
      get().applyTheme();
    } catch {
      // keep existing or null
    } finally {
      set({ brandingLoading: false });
    }
  },

  updateBranding: async (data) => {
    const b = await brandingApi.updateBranding(data);
    set({ branding: b });
    get().applyTheme();
  },

  uploadLogo: async (file) => {
    const { logo_url, favicon_url } = await brandingApi.uploadLogo(file);
    set((s) => ({
      branding: s.branding ? { ...s.branding, logo_url, favicon_url } : null,
    }));
  },

  applyTheme: () => {
    const b = get().branding;
    if (!b) return;
    const root = document.documentElement;
    root.style.setProperty('--color-primary', b.primary_color || '#646cff');
    root.style.setProperty('--color-accent', b.accent_color || '#646cff');
    root.style.setProperty('--color-accent-hover', lighten(b.accent_color || '#646cff', 16));
    if (b.favicon_url) {
      const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (link) link.href = b.favicon_url;
    }
    if (b.app_name) document.title = b.app_name;
  },
});
